/// <reference types="vite-plugin-svgr/client" />
import { useTracking } from '@vp/react-tracking';
import React from 'react';
import { Card, FlexBox, Typography, responsive, useScreenClass } from '@vp/swan';
import { Appointment } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { DesignLiveLogo } from '../assets/DesignLiveLogo';
import { CollaborationListItemFragment } from '../queries/ListingCollaborations.generated';
import { getAnalyticsData } from '../utils/getAnalyticsData';
import { getProjectDescription } from '../utils/getProjectDescription';
import { CTA, RequestCardCTA } from './RequestCardCTA';
import { RequestCardImage } from './RequestCardImage';
import { RequestCardSummary } from './RequestCardSummary';

const formatAppointmentTime = (appointment: Appointment): string => {
    const timezone = appointment.timezone;
    const startTime = appointment.startTime;
    const durationMinutes = appointment.durationMinutes;

    const startDate = new Date(startTime);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

    return `${startDate.toLocaleString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: timezone,
        hour12: true,
    })} - ${endDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
        timeZone: timezone,
        hour12: true,
    })}`;
};

const ResponsiveFlexBox = responsive(FlexBox);

const CollaborationRequestCard: React.FC<CollaborationListItemFragment> = ({
    url,
    thumbnailUrl,
    title,
    status,
    createdAt,
    appointment,
    revisions,
    viewerRole,
    __typename,
}) => {
    const { tracking } = useTracking();

    const section = status !== 'COLLABORATION_STATUS_CANCELLED' ? 'current' : 'past';

    const message = getProjectDescription(status);
    const statusIsInSession = status === 'COLLABORATION_STATUS_APPOINTMENT_IN_SESSION';
    const screenClass = useScreenClass();
    const isXs = screenClass === 'xs';

    const ctas: CTA[] = [
        {
            label: __('Open Project'),
            iconType: 'workWithAPro',
            href: url,
            openInNewTab: false,
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...getAnalyticsData('/zoom', __('Open Project'), section),
                    action: 'Navigation Clicked',
                    navigationDetail: 'open-collaboration',
                });
            },
        },
    ];

    if (statusIsInSession && appointment) {
        ctas.push({
            label: __('Join session with Zoom'),
            iconType: 'phoneCall',
            href: appointment.zoomMeetingUrl,
            openInNewTab: false,
            skin: 'tertiary',
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...getAnalyticsData('/zoom', __('Join session with Zoom')),
                    action: 'Navigation Clicked',
                    navigationDetail: 'join-session',
                });
            },
        });
    }

    if (appointment?.canBeRescheduled) {
        ctas.push({
            label: __('Reschedule'),
            iconType: 'orderHistory',
            href: appointment.rescheduleUrl,
            openInNewTab: false,
            skin: 'tertiary',
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...getAnalyticsData(url, __('Reschedule')),
                    action: 'Navigation Clicked',
                    navigationDetail: 'reschedule-session',
                });
            },
        });
    }

    return (
        <Card bordered>
            <ResponsiveFlexBox
                xs={{
                    flexDirection: 'column',
                    gap: 'between-subsections',
                    alignItems: 'center',
                }}
                sm={{
                    flexDirection: 'row',
                    gap: 'between-sections',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                }}
            >
                <RequestCardImage imgSrc={thumbnailUrl} imgAlt={__('Design Preview')} />

                {/* Collaboration details */}
                <ResponsiveFlexBox
                    style={{ flex: '1 1 100%' }}
                    xs={{
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 'between-subsections',
                    }}
                    sm={{ alignItems: 'flex-start' }}
                >
                    <ResponsiveFlexBox
                        xs={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 'between-actions',
                        }}
                        sm={{ alignItems: 'flex-start' }}
                    >
                        <RequestCardSummary
                            requestType={__typename}
                            title={title}
                            createdAt={createdAt}
                            linkUrl={url}
                            status={status}
                            revisions={revisions}
                        />
                        {viewerRole !== 'VIEWER_ROLE_DESIGNER' && (
                            <Typography
                                fontSkin="body-standard"
                                textAlign={isXs ? 'center' : 'left'}
                            >
                                {message}
                            </Typography>
                        )}
                    </ResponsiveFlexBox>
                    {appointment && (
                        <ResponsiveFlexBox
                            xs={{ flexDirection: 'column', alignItems: 'center', gap: '3' }}
                            sm={{ alignItems: 'flex-start' }}
                        >
                            <DesignLiveLogo />
                            <Typography
                                fontSkin="body-small-bold"
                                textColor="subtle"
                                style={{ textAlign: isXs ? 'center' : 'left' }}
                            >
                                {formatAppointmentTime(appointment)}
                            </Typography>
                        </ResponsiveFlexBox>
                    )}
                </ResponsiveFlexBox>

                {/* Collaboration CTA */}
                <RequestCardCTA ctas={ctas} />
            </ResponsiveFlexBox>
        </Card>
    );
};

export { CollaborationRequestCard };
